import { memo, useEffect, useRef, useState } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Badge } from "@/components/ui/badge";
import type { ClaimMapping } from "@/hooks/useFieldDebate";

export interface CampNodeData {
  label: string;
  description: string;
  positionSummary: string;
  paperCount: number;
  topAuthors: string;
  claims: ClaimMapping[];
  colorIndex: number;
  onClaimClick?: (claimId: string) => void;
  isHighlighted?: boolean;
}

const CAMP_COLORS = [
  { border: "border-green-500", bg: "bg-green-950/30", text: "text-green-400" },
  { border: "border-red-500", bg: "bg-red-950/30", text: "text-red-400" },
  { border: "border-purple-500", bg: "bg-purple-950/30", text: "text-purple-400" },
  { border: "border-blue-500", bg: "bg-blue-950/30", text: "text-blue-400" },
  { border: "border-orange-500", bg: "bg-orange-950/30", text: "text-orange-400" },
  { border: "border-teal-500", bg: "bg-teal-950/30", text: "text-teal-400" },
];

function CampNodeInner({ data }: NodeProps) {
  const d = data as unknown as CampNodeData;
  const colors = CAMP_COLORS[d.colorIndex % CAMP_COLORS.length];

  // The description is clamped to 3 lines on the card (compact, glanceable —
  // the map's value is seeing every camp at once); reveal the full text on
  // hover, but only when it's actually truncated (no redundant popover for
  // short blurbs). scrollHeight > clientHeight means the clamp is cutting text.
  const descRef = useRef<HTMLParagraphElement>(null);
  const [descTruncated, setDescTruncated] = useState(false);
  useEffect(() => {
    const el = descRef.current;
    if (el) setDescTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [d.description]);

  return (
    <div
      className={`rounded-xl border-2 ${colors.border} ${colors.bg} px-4 py-3 shadow-md transition-shadow${d.isHighlighted ? " ring-2 ring-blue-400 ring-offset-1 ring-offset-background" : ""}`}
      style={{ minWidth: 250, maxWidth: 300 }}
    >
      <Handle type="target" position={Position.Top} className="!bg-muted-foreground !w-2 !h-2" />
      <div className="flex items-center justify-between mb-1">
        <span className={`font-bold text-sm ${colors.text}`}>{d.label}</span>
        <Badge variant="secondary" className="text-[10px]">{d.paperCount} papers</Badge>
      </div>
      <div className="group relative mb-2">
        <p
          ref={descRef}
          className={`text-xs text-muted-foreground leading-tight line-clamp-3${descTruncated ? " cursor-help" : ""}`}
        >
          {d.description}
        </p>
        {descTruncated && (
          <div className="absolute left-0 top-full z-50 mt-1 w-[280px] rounded-md border border-border bg-popover px-2.5 py-2 text-[11px] leading-snug text-popover-foreground shadow-lg opacity-0 transition-opacity pointer-events-none group-hover:opacity-100">
            {d.description}
          </div>
        )}
      </div>
      {d.positionSummary && (
        <div className="border-t border-border/40 pt-1 mb-1">
          <p className="text-[10px] text-muted-foreground italic">{d.positionSummary}</p>
        </div>
      )}
      {d.claims.length > 0 && (
        <div className="border-t border-border/40 pt-1 flex items-center gap-2.5 flex-wrap">
          {d.claims.map((claim) => (
            <button
              key={claim.claim_id}
              className="relative group text-blue-400 hover:text-blue-300 cursor-pointer"
              onClick={(e) => { e.stopPropagation(); d.onClaimClick?.(claim.claim_id); }}
            >
              <span className="text-xs">{"\u25C6"}</span>
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2.5 py-1.5 rounded-md bg-popover border border-border shadow-lg text-[11px] text-popover-foreground leading-snug w-56 opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
                <p className="font-medium mb-0.5">{claim.claim_text}</p>
                <p className="text-muted-foreground capitalize">{claim.alignment}</p>
              </div>
            </button>
          ))}
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="!bg-muted-foreground !w-2 !h-2" />
    </div>
  );
}

export const CampNode = memo(CampNodeInner);
